/**
 * ToolCallCards — expandable tool-call transparency cards rendered under
 * assistant messages. Each card shows provider, tool name, status pill,
 * duration, and summary. Expanding a card reveals arguments and a
 * formatted result view. Citation chips in the prose ([call_N]) link to
 * matching cards via DOM id `tool-call-<messageId>-<callId>`.
 *
 * Extracted from TestOpsHub.tsx (lines ~962–1105).
 */

import {
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
} from 'lucide-react'
import clsx from 'clsx'
import type { ToolCallRecord } from '../external-types'
import { FailureAnalysisCard, type DiagnosticFindings } from './FailureAnalysisCard'

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function shortToolName(tool: string, provider: string): string {
  const prefix = `${provider}_`
  const base = tool.startsWith(prefix) ? tool.slice(prefix.length) : tool
  return base.replace(/_/g, ' ')
}

function statusPillClasses(status: ToolCallRecord['status']): string {
  switch (status) {
    case 'success':
      return 'bg-success-soft text-success-foreground border-success/30'
    case 'error':
      return 'bg-destructive-soft text-destructive-foreground border-destructive/30'
    case 'empty':
      return 'bg-foreground/[0.06] text-muted-foreground border-subtle'
    case 'truncated':
      return 'bg-warning-soft text-warning-foreground border-warning/30'
    default:
      return 'bg-accent-soft text-accent-soft-foreground border-accent/30'
  }
}

function statusIcon(status: ToolCallRecord['status']) {
  switch (status) {
    case 'success':
      return <CheckCircle className="h-3.5 w-3.5 text-success-foreground" />
    case 'error':
      return <XCircle className="h-3.5 w-3.5 text-destructive-foreground" />
    case 'empty':
      return <AlertCircle className="h-3.5 w-3.5 text-muted-foreground" />
    case 'truncated':
      return <AlertCircle className="h-3.5 w-3.5 text-warning-foreground" />
    default:
      return <RefreshCw className="h-3.5 w-3.5 animate-spin text-accent" />
  }
}

function formatProviderLabel(provider: string) {
  return provider.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

// ---------------------------------------------------------------------------
// Result formatting — clean key-value display instead of raw JSON
// ---------------------------------------------------------------------------

const NOISE_FIELDS = new Set([
  'node_id', 'permissions', 'topics', 'owner',
  'trees_url', 'statuses_url', 'subscribers_url', 'subscription_url',
  'commits_url', 'git_commits_url', 'git_tags_url', 'git_refs_url',
  'blobs_url', 'tags_url', 'branches_url', 'collaborators_url',
  'comments_url', 'compare_url', 'contents_url', 'contributors_url',
  'deployments_url', 'downloads_url', 'events_url', 'forks_url',
  'git_url', 'hooks_url', 'issue_comment_url', 'issue_events_url',
  'issues_url', 'keys_url', 'labels_url', 'languages_url',
  'merges_url', 'milestones_url', 'notifications_url', 'pulls_url',
  'releases_url', 'stargazers_url', 'archive_url', 'assignees_url',
  'ssh_url', 'svn_url', 'clone_url', 'mirror_url',
])

const VISIBLE_URL_FIELDS = new Set(['html_url', 'web_url', 'homepage', 'url', 'browse_url'])

const LABEL_FIELDS = ['full_name', 'name', 'key', 'title', 'login', 'display_name', 'slug', 'filename', 'path']

function isNoise(key: string): boolean {
  if (NOISE_FIELDS.has(key)) return true
  if (key.endsWith('_url') && !VISIBLE_URL_FIELDS.has(key)) return true
  return false
}

function prettyKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function pickLabel(item: Record<string, unknown>): string {
  for (const f of LABEL_FIELDS) {
    const v = item[f]
    if (v && typeof v === 'string') return v
  }
  return ''
}

function fmtVal(val: unknown): string {
  if (val === null || val === undefined) return '–'
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') return val > 9999 ? val.toLocaleString() : String(val)
  if (typeof val === 'string') return val.length > 120 ? val.slice(0, 117) + '...' : val
  if (Array.isArray(val)) return `${val.length} items`
  if (typeof val === 'object') {
    const inner = val as Record<string, unknown>
    const l = pickLabel(inner)
    if (l) return l
    return Object.entries(inner).slice(0, 3).map(([k, v]) => `${prettyKey(k)}: ${fmtVal(v)}`).join(', ')
  }
  return String(val)
}

/** Render a single dict as compact key-value rows */
function FormattedRecord({ data, index }: { data: Record<string, unknown>; index?: number }) {
  const label = pickLabel(data)
  const fields = Object.entries(data).filter(([k, v]) => {
    if (isNoise(k) || k.startsWith('_')) return false
    if (v === null || v === undefined || v === '') return false
    if (label && typeof v === 'string' && v === label && LABEL_FIELDS.includes(k)) return false
    return true
  })

  return (
    <div className="rounded bg-foreground/[0.04] px-2 py-1.5">
      {label && (
        <div className="flex items-center gap-1.5 mb-1">
          {index !== undefined && (
            <span className="text-[0.6rem] text-subtle-foreground font-mono">{index + 1}.</span>
          )}
          <span className="text-[0.7rem] font-semibold text-foreground truncate">{label}</span>
        </div>
      )}
      {fields.map(([k, v]) => (
        <div key={k} className="flex gap-2 text-[0.65rem] leading-relaxed">
          <span className="shrink-0 text-subtle-foreground min-w-[70px]">{prettyKey(k)}</span>
          <span className="text-muted-foreground break-words">{fmtVal(v)}</span>
        </div>
      ))}
    </div>
  )
}

/** Renders result data as formatted cards instead of raw JSON */
function FormattedResult({ result }: { result: Record<string, unknown> }) {
  const values = Object.values(result)
  const keys = Object.keys(result)

  // Single-key wrapper (backend wraps payloads in {value: ...} or {items: ...})
  // Unwrap to render the inner content directly.
  if (keys.length === 1) {
    const inner = values[0]

    // Wrapper around a list (e.g. {items: [...]} or {repositories: [...]})
    if (Array.isArray(inner)) {
      const items = inner as unknown[]
      return (
        <div className="space-y-1">
          {items.map((item, i) =>
            item && typeof item === 'object' && !Array.isArray(item)
              ? <FormattedRecord key={i} data={item as Record<string, unknown>} index={i} />
              : <div key={i} className="rounded bg-foreground/[0.04] px-2 py-1 text-[0.65rem] text-muted-foreground">{fmtVal(item)}</div>
          )}
        </div>
      )
    }

    // Wrapper around a dict (e.g. {value: {ref, url, object, ...}})
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      return <FormattedRecord data={inner as Record<string, unknown>} />
    }
  }

  // Multi-key object — render as one card
  return <FormattedRecord data={result} />
}

// ---------------------------------------------------------------------------
// ToolCallCards
// ---------------------------------------------------------------------------

export function ToolCallCards({
  messageId,
  records,
  expanded,
  onToggle,
}: {
  messageId: string
  records: ToolCallRecord[]
  expanded: Set<string>
  onToggle: (messageId: string, callId: string) => void
}) {
  if (!records || records.length === 0) return null

  return (
    <div className="mb-3 space-y-1.5">
      {records.map((record) => {
        const key = `${messageId}:${record.call_id}`
        const isOpen = expanded.has(key)
        const durationLabel =
          record.duration_ms >= 1000
            ? `${(record.duration_ms / 1000).toFixed(1)}s`
            : `${record.duration_ms}ms`

        return (
          <div
            id={`tool-call-${messageId}-${record.call_id}`}
            key={key}
            className="rounded-lg border border-subtle bg-card/60 transition"
          >
            <button
              type="button"
              onClick={() => onToggle(messageId, record.call_id)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-foreground/[0.04]"
            >
              {statusIcon(record.status)}
              <span className="font-mono text-[0.7rem] text-subtle-foreground">
                {record.call_id.replace('call_', '#')}
              </span>
              <span className="font-semibold text-foreground">
                {formatProviderLabel(record.provider)}
              </span>
              <span className="text-subtle-foreground">·</span>
              <span className="text-muted-foreground">{shortToolName(record.tool, record.provider)}</span>
              <span
                className={clsx(
                  'ml-1 rounded border px-1.5 py-0.5 text-[0.65rem] font-medium',
                  statusPillClasses(record.status),
                )}
              >
                {record.status}
              </span>
              <span className="ml-auto flex items-center gap-2 text-[0.7rem] text-subtle-foreground">
                <span>{durationLabel}</span>
                {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </span>
            </button>
            <div className="px-3 pb-2 text-[0.75rem] text-muted-foreground">{record.summary}</div>
            {isOpen && (
              <div className="space-y-2 border-t border-subtle px-3 py-2 text-[0.75rem]">
                {Object.keys(record.args || {}).length > 0 && (
                  <div>
                    <div className="mb-1 font-semibold uppercase tracking-wide text-muted-foreground">
                      Arguments
                    </div>
                    <div className="rounded bg-foreground/[0.04] p-2 space-y-0.5">
                      {Object.entries(record.args).map(([k, v]) => (
                        <div key={k} className="flex gap-2 text-[0.65rem]">
                          <span className="shrink-0 text-subtle-foreground font-medium min-w-[70px]">{prettyKey(k)}</span>
                          <span className="text-foreground break-words">{fmtVal(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {record.error ? (
                  <div>
                    <div className="mb-1 font-semibold uppercase tracking-wide text-destructive-foreground">
                      Error
                    </div>
                    <pre className="overflow-x-auto rounded bg-destructive-soft p-2 font-mono text-[0.7rem] text-destructive-foreground">
                      {record.error}
                    </pre>
                  </div>
                ) : record.result ? (
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <span className="font-semibold uppercase tracking-wide text-muted-foreground">
                        Result
                      </span>
                      {record.truncated && (
                        <span className="rounded bg-warning-soft px-1.5 py-0.5 text-[0.65rem] font-medium text-warning-foreground">
                          showing first rows
                        </span>
                      )}
                      {record.empty && (
                        <span className="rounded bg-foreground/[0.06] px-1.5 py-0.5 text-[0.65rem] font-medium text-muted-foreground">
                          empty
                        </span>
                      )}
                    </div>
                    <div className="max-h-[32rem] overflow-auto">
                      {/* Custom render for diagnose_test — the structured
                          findings document is the whole UX. Falls back to
                          the generic key-value renderer otherwise. */}
                      {record.tool === 'testops_diagnose_test' &&
                      (record.result as Record<string, unknown>)?.findings ? (
                        <FailureAnalysisCard
                          findings={
                            (record.result as { findings: DiagnosticFindings }).findings
                          }
                        />
                      ) : (
                        <FormattedResult result={record.result as Record<string, unknown>} />
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
